/**
 * The one control behind all three email stops — everything, one client, one
 * domain. Pausing asks why, because the note is what the next administrator
 * reads when they wonder whether it is safe to resume; resuming asks only to
 * be confirmed.
 *
 * It is rendered only for somebody who may change the stop. Whoever may only
 * look sees the state, which the caller shows.
 */

import { useState } from 'react';
import { Pause, Play } from 'lucide-react';
import { Button, Confirm, Input, Modal, toast } from '@connect/web-shared';

import { refusalText } from './api-refusal';
import type { PauseBody } from './email';

export function PauseButton({
  paused,
  subject,
  consequence,
  resumed,
  size = 'sm',
  onSet,
}: {
  paused: boolean;
  /** What is stopped, as it reads after "Pause" and "Resume": "all email", "example.com". */
  subject: string;
  /** What pausing does, said before it is done. */
  consequence: string;
  /** What resuming does. */
  resumed: string;
  size?: 'sm' | 'md';
  /** Rejects with the API's error when the server refuses. */
  onSet: (body: PauseBody) => Promise<unknown>;
}) {
  const [asking, setAsking] = useState(false);
  const [note, setNote] = useState('');
  const [pending, setPending] = useState(false);
  const [refused, setRefused] = useState<string | undefined>(undefined);

  const close = () => {
    setAsking(false);
    setNote('');
    setRefused(undefined);
  };

  const pause = async () => {
    setPending(true);
    setRefused(undefined);
    try {
      await onSet({ paused: true, note: note.trim() });
      close();
    } catch (error) {
      setRefused(refusalText(error) ?? 'It could not be paused.');
    } finally {
      setPending(false);
    }
  };

  if (paused) {
    return (
      <>
        <Button size={size} variant="secondary" onClick={() => setAsking(true)}>
          <Play className="size-3.5" />
          Resume {subject}
        </Button>
        <Confirm
          open={asking}
          onClose={close}
          title={`Resume ${subject}?`}
          confirmLabel="Resume"
          message={resumed}
          onConfirm={async () => {
            // Said here, and not thrown on: the confirmation has nowhere to
            // show a refusal, and the state on the page still reads "paused".
            try {
              await onSet({ paused: false, note: '' });
            } catch (error) {
              toast.error('Not resumed', refusalText(error));
            }
          }}
        />
      </>
    );
  }

  return (
    <>
      <Button size={size} variant="secondary" onClick={() => setAsking(true)}>
        <Pause className="size-3.5" />
        Pause {subject}
      </Button>
      <Modal
        open={asking}
        onClose={close}
        size="sm"
        title={`Pause ${subject}?`}
        description={consequence}
        footer={
          <div className="flex gap-2">
            <Button variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button loading={pending} onClick={() => void pause()}>
              Pause
            </Button>
          </div>
        }
      >
        <Input
          label="Note for staff"
          maxLength={200}
          placeholder="Bounce rate over 5% on 11 Oct — reviewing"
          hint="Why, for the next administrator. A client is never shown it."
          value={note}
          error={refused}
          onChange={(event) => setNote(event.target.value)}
        />
      </Modal>
    </>
  );
}
